import React from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import { Trans } from 'react-i18next';
import breakpoints from 'styles/themes/wikifactory/styles/breakpoints';
import { remToPxNumber } from 'styles/utils';

import { Link } from 'core/routing';

import Loading from 'components/Loading';
import Heading from 'components/Heading';
import Paragraph from 'components/Paragraph';
import Button from 'components/Button';

import { Divider } from 'legacyComponents/styled/lib';
import {
  OuterWrapper,
  MobileWrapper,
  HeaderWrapper,
  CardsWrapper,
  MobileFooter,
  ErrorWrapper,
} from 'apps/notificationCenter/styles';
import CreateCard from 'apps/notificationCenter/views/createCard';
import FollowCard from 'apps/notificationCenter/views/followCard';
import InviteCard from 'apps/notificationCenter/views/inviteCard';
import InviteActionedCard from 'apps/notificationCenter/views/inviteActionedCard';
import LikeCard from 'apps/notificationCenter/views/likeCard';
import MentionCard from 'apps/notificationCenter/views/mentionCard';
import CommentCard from 'apps/notificationCenter/views/commentCard';
import { HtmlCard } from 'apps/notificationCenter/views/htmlCard';
import { NoNotifications } from 'icons';

export const cardTypes = {
  CREATED: CreateCard,
  FOLLOWED: FollowCard,
  LIKED: LikeCard,
  COMMENTED: CommentCard,
  MENTIONED: MentionCard,
  INVITED: InviteCard,
  INVITE_ACTIONED: InviteActionedCard,
  HTML: HtmlCard,
};

export const errorMessage =
  'Ooops! There was an error displaying your notifications!';

type NotificationCenterProps = {
  notificationCenter?: Record<string, any>;
  isMobile?: boolean;
  visible: boolean;
  onClose: () => void;
};

@inject('notificationCenter')
@observer
export class NotificationCenter extends React.Component<NotificationCenterProps> {
  [x: string]: any;
  constructor(props: any) {
    super(props);
    this.notifRef = React.createRef();
  }

  componentDidMount() {
    this.props.notificationCenter?.NewNotificationsQuery();
  }

  componentDidCatch(error: any, info: any) {
    this.props.notificationCenter.CatchError(error, info);
  }

  isDesktop = () => {
    const breakpoint = remToPxNumber(breakpoints.large) || 945;
    return (
      Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0,
      ) >= breakpoint
    );
  };

  render() {
    const { notificationCenter, isMobile, visible, onClose } = this.props;

    if (!visible) return null;

    const shouldShowEmpty = !notificationCenter?.notificationsConn?.totalCount;

    const emptyContent = (
      <div className='flex flex-col justify-center items-center mt-4'>
        <NoNotifications />
        <h4 className='mt-2 text-black'>No Notifications yet.</h4>
      </div>
    );

    if (notificationCenter.hasError) {
      const hasErrorContent = (
        <ErrorWrapper>
          <Paragraph>
            <Trans>{errorMessage}</Trans>
          </Paragraph>
        </ErrorWrapper>
      );

      if (isMobile) return <MobileWrapper>{hasErrorContent}</MobileWrapper>;

      return (
        <OuterWrapper ref={this.notifRef} visible={visible}>
          <HeaderWrapper>
            <Heading level='4'>
              <Trans>Notifications</Trans>
            </Heading>
          </HeaderWrapper>
          <Divider width='100%' />
          {hasErrorContent}
        </OuterWrapper>
      );
    }

    if (notificationCenter.loading) {
      if (isMobile)
        return (
          <MobileWrapper>
            <Loading />
          </MobileWrapper>
        );

      return (
        <OuterWrapper visible={visible}>
          <Loading />
        </OuterWrapper>
      );
    }

    const { notifications, pendingCount, notificationsConn } =
      notificationCenter;

    const notificationsWrapper = notifications ? (
      <CardsWrapper>
        {[...new Set(notifications)].slice(0, 10).map((notif, index) => {
          const Card = cardTypes[notif.type];
          const key = notif.id || `${notif.type}_${index}`;
          return Card && <Card key={key} id={key} notification={notif} />;
        })}
      </CardsWrapper>
    ) : (
      <CardsWrapper>
        <Paragraph>
          <Trans>You&amaragraphs;re all cought up!</Trans>
        </Paragraph>
      </CardsWrapper>
    );

    const showAll = (
      <Link name='home.notifications'>
        <Button
          onClick={onClose}
          style={{ marginTop: 16 }}
          outline={!isMobile}
          square={!isMobile}
          fullWidth={!isMobile}
          appearance={isMobile ? 'plain' : 'default'}
          variant={isMobile ? 'tertiary' : 'primary'}
          text={
            <Trans>
              {pendingCount ? `Show pending (${pendingCount})` : 'Show all'}
            </Trans>
          }
        />
      </Link>
    );

    if (isMobile) {
      return (
        <MobileWrapper>
          {shouldShowEmpty ? emptyContent : notificationsWrapper}
          <MobileFooter>
            {showAll}
            <Button
              appearance='plain'
              size='small'
              text={!shouldShowEmpty && <Trans>Mark all as Read</Trans>}
              onClick={() => notificationCenter?.MarkAllNotificationsAsRead()}
            />
          </MobileFooter>
        </MobileWrapper>
      );
    }

    return (
      <OuterWrapper ref={this.notifRef} visible={visible}>
        <HeaderWrapper>
          <Heading level='4'>
            <Trans>Notifications</Trans>
          </Heading>
          <Button
            appearance='plain'
            size='small'
            text={
              !shouldShowEmpty && (
                <Trans>
                  <span className='font-normal'>Mark all as Read</span>
                </Trans>
              )
            }
            onClick={() => notificationCenter?.MarkAllNotificationsAsRead()}
          />
        </HeaderWrapper>
        <Divider width='100%' />
        {shouldShowEmpty ? emptyContent : notificationsWrapper}
        {notificationsConn.hasNextPage && showAll}
      </OuterWrapper>
    );
  }
}

NotificationCenter.defaultProps = {
  isMobile: false,
};

NotificationCenter.propTypes = {
  isMobile: PropTypes.bool,
  visible: PropTypes.bool,
  notificationCenter: PropTypes.object,
};
